<template>
  <TheFieldset legend="Filter Submissions">
    <form class="filter__form">
      <div class="filter__form__wrapper">
        <div class="filter__form__row">
          <TheDatepicker
            label="From"
            v-model="state.from"
            class="field mw-300"
          />
          <NewSelect
            v-model="state.districts"
            fluid
            multiple
            filter
            :maxSelectedLabels="3"
            :options="districtOptions"
            label="Select Districts"
            id="districtsSelect"
            class="field mw-300"
          />
          <NewSelect
            v-model="state.dealerships"
            fluid
            multiple
            filter
            :maxSelectedLabels="3"
            :options="dealershipsOptions"
            label="Select Dealerships"
            id="dealershipsSelect"
            class="field"
          />
          <NewSelect
            v-model="state.status"
            fluid
            multiple
            :maxSelectedLabels="3"
            :options="statusOptions"
            label="Select Statuses"
            id="statusSelect"
            class="field mw-200"
          />
        </div>
        <div class="filter__form__row">
          <TheDatepicker
            label="To"
            v-model="state.to"
            class="field mw-300"
          />
          <NewSelect
            v-model="state.zones"
            fluid
            multiple
            filter
            :maxSelectedLabels="3"
            :options="zoneOptions"
            label="Select Zones"
            id="statesSelect"
            class="field mw-300"
          />
          <NewSelect
            v-model="state.lastNames"
            fluid
            multiple
            filter
            :maxSelectedLabels="3"
            :options="lastNamesOptions"
            label="Select Last Names"
            id="namesSelect"
            class="field"
          />
          <div class="checkbox-wrapper">
            <TheCheckbox
              v-model="state.acura"
              id="submission-acura"
              text="Acura "
            />
            <TheCheckbox
              v-model="state.honda"
              id="submission-honda"
              text="Honda "
            />
          </div>
        </div>
      </div>
      <div class="buttons__wrapper">
        <GlowButton type="button" class="border-2" @click="submitForm">
          Search
        </GlowButton>
        <GlowButton type="button" class="border-2" @click="clearFilters">
          Clear Filters
        </GlowButton>
      </div>
    </form>
  </TheFieldset>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useToast } from 'vue-toastification';

import api from '@/api';
import TheCheckbox from '@/components/TheCheckbox.vue';
import TheFieldset from '@/components/TheFieldset.vue';
import TheDatepicker from '@/components/TheDatepicker.vue';
import NewSelect from '@/components/NewSelect.vue';
import GlowButton from '@/components/GlowButton.vue';

const toast = useToast();

defineProps({
  showFilters: Boolean,
});

const emit = defineEmits(['applyFilters']);

const state = ref({
  from: '',
  to: '',
  districts: [],
  status: [],
  dealerships: [],
  zones: [],
  lastNames: [],
  acura: true,
  honda: true,
});

const districtOptions = ref([]);
const dealershipsOptions = ref([]);
const zoneOptions = ref([]);
const lastNamesOptions = ref([]);
const statusOptions = ref(['Reviewed', 'Rejected', 'No Status Set']);

const fetchDistricts = async () => {
  try {
    const { data } = await api.data.getDistrictLetters(null, true);
    districtOptions.value = data;
  } catch {
    toast.error('An error ocurred while fetching Districts. Please try again.');
  }
};
const fetchZoneCodes = async () => {
  try {
    const { data } = await api.data.getZoneCodes(null, true);
    zoneOptions.value = data;
  } catch {
    toast.error('An error ocurred while fetching Zone Codes. Please try again.');
  }
};

const fetchDealerships = async () => {
  try {
    const { data } = await api.data.getDealerships(null, true);
    dealershipsOptions.value = data;
  } catch {
    toast.error('An error ocurred while fetching Zone Codes. Please try again.');
  }
};

const fetchLastNames = async () => {
  try {
    const { data } = await api.data.getLastNames();
    lastNamesOptions.value = data;
  } catch {
    toast.error('An error ocurred while fetching Last Names. Please try again.');
  }
};

const submitForm = () => {
  /* eslint-disable-next-line */
  const filtersToApply = Object.fromEntries(Object.entries(state.value).filter(([key, value]) => !!value && value.length > 0));

  if (state.value.from) {
    filtersToApply.from = state.value.from.toISOString().replace('.000', '');
  }

  if (state.value.to) {
    filtersToApply.to = state.value.to.toISOString().replace('.000', '');
  }

  filtersToApply.acura = state.value.acura;
  filtersToApply.honda = state.value.honda;

  emit('applyFilters', filtersToApply);
};

const clearFilters = () => {
  state.value = {
    from: '',
    to: '',
    districts: [],
    dealerships: [],
    statuses: [],
    zones: [],
    lastNames: [],
    acura: true,
    honda: true,
  };

  emit('applyFilters', state.value);
};

onMounted(() => {
  fetchDistricts();
  fetchZoneCodes();
  fetchDealerships();
  fetchLastNames();
});
</script>

<style scoped lang="scss">
.filter {
  font-family: 'Avenir';

  &__form {
    display: flex;
    flex-wrap: wrap;
    gap: 20px 40px;

    &__wrapper {
      display: flex;
      flex-direction: column;
      gap: 20px;
      flex: 1 1 1140px;
    }

    &__row {
      display: flex;
      gap: 10px;
      align-items: center;
    }

    .checkbox-wrapper {
      display: flex;
      gap: 10px;
      padding-left: 10px;

      :deep(.custom-checkbox__text) {
        font-family: 'Avenir';
      }
    }

    .field {
      height: 40px;
      flex: 1;
    }

    .mw-200 {
      max-width: 200px;
    }

    .mw-300 {
      max-width: 300px;
    }
  }
}

.buttons__wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;

  :deep(.get-started-btn) {
    font-family: 'Avenir';
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 1px;
    text-align: center;
    padding: 4px 16px 2px;
    width: 250px;
    height: 45px;
    border: none;
  }
}

:deep(.p-multiselect) {
  .p-multiselect-label-container {
    height: 40px;
  }
}

:deep(.p-floatlabel) {
  label {
    margin-top: -6px;
    font-family: 'Avenir';
  }

  &:has(input:focus) label,
  &:has(input.p-filled) label,
  &:has(input:-webkit-autofill) label,
  &:has(textarea:focus) label,
  &:has(textarea.p-filled) label,
  &:has(.p-inputwrapper-focus) label,
  &:has(.p-inputwrapper-filled) label,
  .p-multiselect-open + label,
  .p-focus + label {
    top: -8px;
    left: 5px;
  }
}
</style>
